import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Button,
  Box,
  Select,
  MenuItem,
  Pagination,
  FormControl,
  TextField, // Added for search input
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";

const getButtonColor = (status) => {
  switch (status) {
    case 1:
      return "#FFC107";
    case 2:
      return "#4CAF50";
    case 3:
      return "#F44336";
    case 4:
      return "#9E9E9E";
    default:
      return "#a7e2f9";
  }
};

const TreatmentManagement = () => {
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalDentalTreatment, setTotalDentalTreatments] = useState(0);

  // New search state
  const [keyword, setKeyword] = useState("");

  // Determine the status based on the current tab value.
  const status = value === 0 ? 1 : value === 1 ? 2 : value === 2 ? 3 : 4;

  // Updated fetch hook including keyword, page and rowsPerPage
  const { data, isLoading, refetch } = useFetchObjects(
    ["dental-treatments", status, page, rowsPerPage, keyword],
    `dental-treatments/?page=${page}&page_size=${rowsPerPage}&status=${status}&search=${keyword}`,
    token
  );

  const { handleUpdate } = useUpdate("dental-treatments", token);

  // Refetch whenever pagination, status, or search keyword changes
  useEffect(() => {
    refetch();
  }, [refetch, page, rowsPerPage, keyword, status]);

  // Update total treatment count when data changes
  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalDentalTreatments(data.count);
    }
  }, [data]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // When the tab value changes, update the status and reset page
  const handleChange = (event, newValue) => {
    setValue(newValue);
    setPage(1);
  };

  const handleStatusChange = (treatmentId, newStatus) => {
    const formData = new FormData();
    formData.append("status", newStatus);

    handleUpdate(treatmentId, formData);
    refetch();
  };

  const statusLabels = {
    1: t("dental.pending"),
    2: t("dental.inProgress"),
    3: t("dental.completed"),
    4: t("dental.cancelled"),
  };

  const Row = ({ treatment }) => {
    const [open, setOpen] = useState(false);

    return (
      <>
        <TableRow>
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>{treatment.treatment_date}</TableCell>
          <TableCell>{treatment.patient_name}</TableCell>
          <TableCell>{treatment.dentist_name}</TableCell>
          <TableCell>
            {treatment.total_cost} ؋{" "}
            {treatment.discount > 0 && (
              <Typography
                component="span"
                sx={{
                  fontStyle: "italic",
                  fontSize: "0.9rem",
                  color: "gray",
                  marginLeft: "5px",
                }}
              >
                ({t("dental.discount")}: {treatment.discount} ؋)
              </Typography>
            )}
          </TableCell>
          <TableCell>
            <Typography
              sx={{
                fontWeight: "bold",
                color: getButtonColor(treatment.status),
              }}
            >
              {statusLabels[treatment.status]}
            </Typography>
          </TableCell>
          <TableCell>
            {treatment.status === 1 && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    backgroundColor: "#4CAF50",
                    textTransform: "none",
                    fontWeight: "bold",
                    margin: "0 8px",
                  }}
                  onClick={() => handleStatusChange(treatment.id, 2)}
                >
                  {t("dental.markInProgress")}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    backgroundColor: "#F44336",
                    textTransform: "none",
                    fontWeight: "bold",
                    margin: "0 8px",
                  }}
                  onClick={() => handleStatusChange(treatment.id, 3)}
                >
                  {t("dental.markCompleted")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    borderColor: "#F44336",
                    color: "#F44336",
                    margin: "0 8px",
                  }}
                  onClick={() => handleStatusChange(treatment.id, 4)}
                >
                  {t("dental.markCancelled")}
                </Button>
              </>
            )}
            {treatment.status === 2 && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    backgroundColor: "#F44336",
                    textTransform: "none",
                    fontWeight: "bold",
                    margin: "0 8px",
                  }}
                  onClick={() => handleStatusChange(treatment.id, 3)}
                >
                  {t("dental.markCompleted")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    borderColor: "#F44336",
                    color: "#F44336",
                    margin: "0 8px",
                  }}
                  onClick={() => handleStatusChange(treatment.id, 4)}
                >
                  {t("dental.markCancelled")}
                </Button>
              </>
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={7} style={{ paddingBottom: 0, paddingTop: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom component="div">
                  {t("dental.procedures")}
                </Typography>
                <Table size="small" aria-label="details">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("dental.procedureName")}</TableCell>
                      <TableCell>{t("dental.tooth")}</TableCell>
                      <TableCell>{t("dental.cost")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {treatment.treatment_details.map((detail) => (
                      <TableRow key={detail.procedure_name}>
                        <TableCell>{detail.procedure_name}</TableCell>
                        <TableCell>{detail.tooth_number}</TableCell>
                        <TableCell>{detail.cost} ؋</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ boxShadow: "0px 0px 0px" }}>
        <Grid container padding={"10px"} spacing={2}>
          <Grid item xs={12} sm={6}>
            <Tabs value={value} onChange={handleChange}>
              <Tab
                sx={{ textTransform: "capitalize" }}
                label={t("dental.pending")}
              />
              <Tab
                sx={{ textTransform: "capitalize" }}
                label={t("dental.inProgress")}
              />
              <Tab
                sx={{ textTransform: "capitalize" }}
                label={t("dental.completed")}
              />
              <Tab
                sx={{ textTransform: "capitalize" }}
                label={t("dental.cancelled")}
              />
            </Tabs>
          </Grid>
          {/* Search Field */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={t("dental.search")}
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ mt: 2 }}>
              {isLoading ? (
                <Typography>{t("dental.loading")}</Typography>
              ) : (
                <Table>
                  <TableHead style={{ backgroundColor: "#f9f9f9" }}>
                    <TableRow>
                      <TableCell />
                      <TableCell>{t("dental.date")}</TableCell>
                      <TableCell>{t("dental.patient")}</TableCell>
                      <TableCell>{t("dental.dentist")}</TableCell>
                      <TableCell>{t("dental.totalCostWithDiscount")}</TableCell>
                      <TableCell>{t("dental.status")}</TableCell>
                      <TableCell>{t("dental.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {data?.results?.map((treatment) => (
                      <Row key={treatment.id} treatment={treatment} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Grid>
        </Grid>
        {/* Pagination Controls */}
        <Grid item xs={12} m={3} pb={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={4}
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body2" mr={2}>
                {t("reception.rowsPerPage")}:
              </Typography>
              <FormControl variant="standard" sx={{ minWidth: 120 }}>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  label={t("reception.rowsPerPage")}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalDentalTreatment / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default TreatmentManagement;
